import React, { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Plus,
  Edit2,
  Trash2,
  Tag,
  Save,
  X,
  RefreshCw,
  AlertTriangle,
  Check,
  Upload,
  Image as ImageIcon,
} from "lucide-react";
import { apiGet, apiGetWithMeta, apiPost, apiPut, apiDelete, apiUpload } from "../lib/api";
import type { Category } from "@buddys-choice/shared";
import { CreateCategoryInput, categorySlugFromName } from "@buddys-choice/shared";
import type { CreateCategoryInput as CreateCategoryInputType } from "@buddys-choice/shared";
import { DataTablePagination } from "./DataTablePagination";
import { AppDataTable } from "./AppDataTable";
import { ProductImage } from "./ProductImage";

const emptyForm: CreateCategoryInputType = {
  name: "",
  navLabel: "",
  description: "",
  imageUrl: "",
  sortOrder: 0,
  isActive: true,
};

export const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingSlug, setEditingSlug] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateCategoryInputType>({
    resolver: zodResolver(CreateCategoryInput) as import("react-hook-form").Resolver<CreateCategoryInputType>,
    defaultValues: emptyForm,
  });
  const watchedName = watch("name");
  const watchedImageUrl = watch("imageUrl");

  const fetchCategories = () => {
    setLoading(true);
    apiGetWithMeta<Category[]>("categories/manage", { page, pageSize })
      .then((res) => {
        setCategories(res.data || []);
        setTotal(res.meta?.total ?? 0);
        setTotalPages(res.meta?.totalPages ?? 1);
      })
      .catch((err) => {
        console.error("Fetch categories error:", err);
        setError(
          "Failed to load categories. Ensure API is running and migrations are applied.",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategories();
  }, [page]);

  const openCreate = () => {
    setEditingId(null);
    setEditingSlug("");
    reset(emptyForm);
    setError("");
    setSuccessMsg("");
    setShowForm(true);
  };

  const openEdit = (cat: Category) => {
    setEditingId(cat.id);
    setEditingSlug(cat.slug);
    reset({
      name: cat.name,
      navLabel: cat.navLabel ?? "",
      description: cat.description ?? "",
      imageUrl: cat.imageUrl ?? "",
      sortOrder: cat.sortOrder ?? 0,
      isActive: cat.isActive,
    });
    setError("");
    setSuccessMsg("");
    setShowForm(true);
  };

  const onSubmit = async (data: CreateCategoryInputType) => {
    setError("");
    setSuccessMsg("");
    try {
      if (editingId) {
        await apiPut(`categories/${editingId}`, data);
        setSuccessMsg("Category updated successfully.");
      } else {
        await apiPost("categories", data);
        setSuccessMsg("Category created successfully.");
      }
      setShowForm(false);
      fetchCategories();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to save category";
      setError(msg);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    setError("");
    try {
      await apiDelete(`categories/${id}`);
      setDeleteConfirmId(null);
      setSuccessMsg("Category deleted.");
      fetchCategories();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to delete category";
      setError(msg);
      setDeleteConfirmId(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleCategoryImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      setError("");
      const form = new FormData();
      form.append("file", file);
      const res = await apiUpload<{ url: string } | { success: boolean; data: { url: string } }>("uploads", form);
      const url = (res as any)?.data?.url || (res as any)?.url;
      if (url) {
        setValue("imageUrl", url, { shouldValidate: true });
        setSuccessMsg("Category image uploaded to R2 successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err: unknown) {
      setError("Failed to upload category image: " + ((err as Error).message || "Upload failed"));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const categoryColumns = useMemo(
    () => [
      {
        name: "Image",
        cell: (cat: Category) => (
          <div className="w-10 h-10 rounded-xl overflow-hidden bg-[var(--bg-surface-2)] border border-[var(--border)] shrink-0 flex items-center justify-center">
            {cat.imageUrl ? (
              <ProductImage
                src={cat.imageUrl}
                alt={cat.name}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
              />
            ) : (
              <Tag className="w-4 h-4 text-[var(--gold-main)]/50" />
            )}
          </div>
        ),
      },
      {
        name: "Order",
        sortable: true,
        sortValue: (cat: Category) => cat.sortOrder,
        selector: (cat: Category) => (
          <span className="font-mono text-[var(--text-muted)]">{cat.sortOrder}</span>
        ),
      },
      {
        name: "Slug",
        sortable: true,
        sortValue: (cat: Category) => cat.slug,
        selector: (cat: Category) => (
          <span className="font-mono text-[var(--gold-main)]">{cat.slug}</span>
        ),
      },
      {
        name: "Name",
        sortable: true,
        sortValue: (cat: Category) => cat.name,
        selector: (cat: Category) => (
          <span className="font-medium">{cat.name}</span>
        ),
      },
      {
        name: "Nav Label",
        selector: (cat: Category) => cat.navLabel || "—",
      },
      {
        name: "Products",
        sortable: true,
        sortValue: (cat: Category) => cat.productCount ?? 0,
        selector: (cat: Category) => cat.productCount ?? 0,
      },
      {
        name: "Status",
        cell: (cat: Category) => (
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
              cat.isActive
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                : "bg-[var(--bg-surface-2)] text-[var(--text-faint)] border border-[var(--border)]"
            }`}
          >
            {cat.isActive ? "Active" : "Hidden"}
          </span>
        ),
      },
      {
        name: "Actions",
        right: true,
        cell: (cat: Category) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => openEdit(cat)}
              className="p-2 rounded-lg hover:bg-[var(--bg-surface-2)] text-[var(--text-muted)] hover:text-[var(--gold-main)]"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setDeleteConfirmId(cat.id)}
              disabled={(cat.productCount ?? 0) > 0}
              className="p-2 rounded-lg hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
              title={
                (cat.productCount ?? 0) > 0
                  ? "Reassign products before deleting"
                  : "Delete"
              }
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-[var(--text-primary)] flex items-center gap-2">
            <Tag className="w-5 h-5 text-[var(--gold-main)]" />
            Category Catalogue
          </h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Manage storefront navigation, product filters, and collection labels
            dynamically.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchCategories}
            className="btn-gold-outline text-xs py-2 px-4 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="btn-gold text-xs py-2.5 px-5 font-bold flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs">
          <Check className="w-4 h-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="glass-luxury-card p-5 sm:p-6 space-y-4 border border-[var(--gold-soft-border)]"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-[var(--text-primary)]">
              {editingId ? "Edit Category" : "New Category"}
            </h3>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="p-2 rounded-lg hover:bg-[var(--bg-surface-2)] text-[var(--text-muted)]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                Display Name *
              </label>
              <input
                type="text"
                {...register("name")}
                placeholder="e.g. Gold Plated Bangles"
                aria-invalid={!!errors.name}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none"
              />
              {errors.name && (
                <span className="text-[11px] text-red-400 mt-1 block">{errors.name.message}</span>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                Slug *{" "}
                <span className="text-[var(--text-faint)] normal-case">
                  (used in URLs & products)
                </span>
              </label>
              <input
                type="text"
                value={editingId ? editingSlug : categorySlugFromName(watchedName || "")}
                readOnly
                disabled
                placeholder="AUTO-GENERATED"
                className="w-full bg-[var(--bg-surface-2)] border border-[var(--border)] rounded-xl py-3 px-4 text-xs text-[var(--text-muted)] outline-none cursor-not-allowed uppercase font-mono"
              />
              <p className="text-[10px] text-[var(--text-faint)] mt-1">
                Auto-generated from name in uppercase. Cannot be edited after
                creation.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                Nav Label
              </label>
              <input
                type="text"
                {...register("navLabel")}
                placeholder="Short label for header menu"
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                Sort Order
              </label>
              <input
                type="number"
                min={0}
                {...register("sortOrder", { valueAsNumber: true })}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
              Category Image & Banner
            </label>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-xl border border-[var(--border)] bg-[var(--bg-main)]">
              {/* Image Preview */}
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-[var(--bg-surface-2)] border border-[var(--border)] shrink-0 flex items-center justify-center relative group">
                {watchedImageUrl ? (
                  <ProductImage
                    src={watchedImageUrl}
                    alt="Category preview"
                    className="w-full h-full"
                    imgClassName="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-[var(--text-faint)]" />
                )}
              </div>

              <div className="flex-1 space-y-2 w-full min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCategoryImageUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    disabled={uploadingImage}
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-gold-outline text-xs py-1.5 px-3 rounded-lg flex items-center gap-1.5"
                  >
                    {uploadingImage ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    <span>{uploadingImage ? "Uploading..." : "Upload Image to R2"}</span>
                  </button>

                  {watchedImageUrl && (
                    <button
                      type="button"
                      onClick={() => setValue("imageUrl", "", { shouldValidate: true })}
                      className="text-xs text-red-400 hover:underline px-2"
                    >
                      Remove Image
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  {...register("imageUrl")}
                  placeholder="Or enter image URL (e.g. https://... or /categories/rings.webp)"
                  className="w-full bg-[var(--bg-surface-2)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-lg py-1.5 px-3 text-[11px] font-mono text-[var(--text-primary)] outline-none"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
              Description
            </label>
            <textarea
              {...register("description")}
              rows={2}
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none resize-none"
            />
            {errors.description && (
              <span className="text-[11px] text-red-400 mt-1 block">{errors.description.message}</span>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-[var(--text-primary)] cursor-pointer">
            <input
              type="checkbox"
              {...register("isActive")}
              className="rounded border-[var(--border)] text-[var(--gold-main)] focus:ring-[var(--gold-main)]"
            />
            Active (visible on storefront when products exist)
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="btn-gold-outline text-xs py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-gold text-xs py-2.5 px-6 font-bold flex items-center gap-2"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {editingId ? "Update Category" : "Create Category"}
            </button>
          </div>
        </form>
      )}

      <div className="glass-luxury-card overflow-hidden">
        <AppDataTable
          columns={categoryColumns}
          data={categories}
          keyField={(c) => c.id}
          progressPending={loading}
          noDataComponent="No categories yet. Add your first category to organize products."
        />
      </div>

      <DataTablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        className="pt-2"
      />

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--bg-main)]/80 backdrop-blur-sm">
          <div className="glass-luxury-card p-6 max-w-md w-full space-y-4">
            <h3 className="text-sm font-bold text-[var(--text-primary)]">
              Delete Category?
            </h3>
            <p className="text-xs text-[var(--text-muted)]">
              Categories with assigned products cannot be deleted. Reassign or
              remove those products first.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="btn-gold-outline text-xs py-2 px-4"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleting}
                className="text-xs py-2 px-4 rounded-xl bg-red-500/20 text-red-400 border border-red-500/40 font-bold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
